import classNames from 'classnames';
import { MouseEventHandler, useCallback, useMemo, useState } from 'react';
import { attempt } from 'yoso';
import {
  DropdownContext,
  DropdownContextValue,
  DropdownOptions,
} from '~/components/Dropdown/Dropdown.hooks';
import { useHandleBodyClick } from '~/components/Modal/Modal.hooks';
import styles from './DropdownRoot.module.scss';

export type DropdownRootProps = {
  active?: boolean;
} & DropdownOptions &
  JSX.IntrinsicElements['div'];

export const DropdownRoot = ({
  active: activeProp,
  placement = 'bottom',
  closeOnClick = false,
  className,
  children,
  onClick,
  ...props
}: DropdownRootProps) => {
  const stateless = activeProp !== undefined;
  const [active, setActive] = useState(false);

  const context = useMemo<DropdownContextValue>(() => {
    if (stateless) {
      return {
        active: activeProp,
        placement,
        open: () => true,
        close: () => false,
      };
    }

    return {
      active,
      placement,
      open: () => {
        setActive(true);
      },
      close: () => {
        setActive(false);
      },
    };
  }, [active, activeProp, placement, stateless]);

  const { ref } = useHandleBodyClick({
    onClick: () => {
      if (!context.active) return;
      context.close();
    },
  });

  const handleClick = useCallback<MouseEventHandler<HTMLDivElement>>(
    (event) => {
      if (closeOnClick && context.active) context.close();
      onClick?.(event);
    },
    [closeOnClick, onClick, context]
  );

  return (
    <DropdownContext.Provider value={context}>
      <div
        ref={ref}
        className={classNames(styles.root, className)}
        onClick={handleClick}
        {...props}>
        {attempt(children, context)}
      </div>
    </DropdownContext.Provider>
  );
};
